import React, { useState, useEffect, useCallback } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { getTransactionData, saveSignedMessage } from "../../api/web3Api";
import { completeInquiry } from "../../api/kycApi";
import "./Web3Connect.css";
import { ethers } from "ethers";

function Web3Connect({ inquiryId, onBack, onContinue, token }) {
  const [step, setStep] = useState("connect"); // connect, signing, success, error
  const [transactionData, setTransactionData] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();

  const fetchTransactionData = useCallback(async () => {
    if (!address) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await getTransactionData(inquiryId, address, token);
      if (response?.statusCode === 200) {
        setTransactionData(response.data.data);
        setStep("signing");
      } else {
        setError("Failed to get transaction data");
      }
    } catch (err) {
      console.error(
        "Error fetching transaction data:",
        err.response.data.error.message
      );
      // Extract specific error message from backend if available
      const errorMessage =
        err.response.data.error.message ||
        // err.response?.data?.error ||
        // err.message ||
        "Failed to get transaction data";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [inquiryId, address, token]);

  // When wallet connects, fetch transaction data
  useEffect(() => {
    if (isConnected && address && step === "connect") {
      fetchTransactionData();
    }
    setError(null);
  }, [isConnected, address, step, fetchTransactionData]);

  const handleSign = async () => {
    if (!transactionData) return;

    setIsLoading(true);
    setError(null);
    try {
      // Sign the message hash
      const signature = await signMessageAsync({
        message: { raw: ethers.getBytes(transactionData.messageHash) },
      });

      // Save signed message to API
      const saveResponse = await saveSignedMessage(
        inquiryId,
        {
          ...transactionData,
          signature,
        },
        token
      );

      if (saveResponse?.statusCode === 201) {
        // Call complete inquiry API
        const completeResponse = await completeInquiry(inquiryId, token);

        setStep("success");
        setTimeout(() => {
          onContinue();
        }, 2000);
      } else {
        setError("Failed to save signed message");
      }
    } catch (err) {
      console.error("Error signing message:", err);
      setError(err.message || "Failed to sign message");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="web3-container">
        <div className="web3-card">
          <div className="web3-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "95%" }}
              ></div>
            </div>
          </div>

          <div className="web3-icon-wrapper">
            <svg className="web3-icon" viewBox="0 0 100 100" fill="none">
              <circle
                cx="50"
                cy="50"
                r="40"
                stroke="#121212"
                strokeWidth="3"
                fill="none"
              />
              <path
                d="M50 25 L70 45 L50 55 L30 45 Z"
                fill="#121212"
                opacity="0.8"
              />
              <path
                d="M50 60 L70 50 L50 75 L30 50 Z"
                fill="#121212"
                opacity="0.6"
              />
            </svg>
          </div>

          <h2 className="web3-title">Connect Your Wallet</h2>
          <p className="web3-subtitle">
            Connect your wallet to complete the identity attestation
          </p>

          {error && (
            <div className="web3-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          <div className="connect-button-wrapper">
            <ConnectButton.Custom>
              {({ openConnectModal }) => (
                <button className="web3-connect-btn" onClick={openConnectModal}>
                  Connect Wallet
                </button>
              )}
            </ConnectButton.Custom>
          </div>

          <p className="web3-note">
            Supports MetaMask, WalletConnect, Coinbase Wallet and more
          </p>

          <div className="web3-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  // Loading Transaction Data or Error state
  if (step === "connect" && isConnected) {
    if (isLoading) {
      return (
        <div className="web3-container">
          <div className="web3-card">
            <div className="web3-header">
              <button className="back-button" onClick={onBack}>
                ←
              </button>
              <div className="header-progress">
                <div
                  className="header-progress-fill"
                  style={{ width: "95%" }}
                ></div>
              </div>
            </div>

            <div className="web3-loading">
              <div className="loading-spinner"></div>
              <h2 className="web3-title">Preparing Transaction</h2>
              <p className="web3-subtitle">
                Please wait while we prepare your attestation data...
              </p>
            </div>

            <div className="web3-footer">Secured by VerifiLite</div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="web3-container">
          <div className="web3-card">
            <div className="web3-header">
              <button className="back-button" onClick={onBack}>
                ←
              </button>
              <div className="header-progress">
                <div
                  className="header-progress-fill"
                  style={{ width: "95%" }}
                ></div>
              </div>
            </div>

            <div className="wallet-info">
              <div className="wallet-address">
                <span className="wallet-label">Connected Wallet</span>
                <span className="address">
                  {address?.slice(0, 6)}...{address?.slice(-4)}
                </span>
              </div>
              <ConnectButton.Custom>
                {({ openAccountModal }) => (
                  <button className="disconnect-btn" onClick={openAccountModal}>
                    Change
                  </button>
                )}
              </ConnectButton.Custom>
            </div>

            <div className="web3-error-content">
              <div className="error-icon-wrapper">
                <svg className="error-icon" viewBox="0 0 100 100" fill="none">
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    stroke="#dc2626"
                    strokeWidth="3"
                    fill="none"
                  />
                  <path
                    d="M35 35 L65 65 M65 35 L35 65"
                    stroke="#dc2626"
                    strokeWidth="5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <h2 className="web3-title">Transaction Error</h2>
              <div className="web3-error">
                <span>⚠️ {error}</span>
              </div>
              <p className="web3-subtitle">
                There was a problem preparing your transaction. Please check
                your wallet address or try again.
              </p>

              <button className="web3-retry-btn" onClick={fetchTransactionData}>
                Retry Preparation
              </button>
            </div>

            <div className="web3-footer">Secured by VerifiLite</div>
          </div>
        </div>
      );
    }
  }

  // Sign Message Screen
  if (step === "signing") {
    return (
      <div className="web3-container">
        <div className="web3-card">
          <div className="web3-header">
            <button className="back-button" onClick={onBack}>
              ←
            </button>
            <div className="header-progress">
              <div
                className="header-progress-fill"
                style={{ width: "95%" }}
              ></div>
            </div>
          </div>

          <h2 className="web3-title">Sign Attestation</h2>
          <p className="web3-subtitle">
            Sign the message to complete your identity attestation
          </p>

          <div className="wallet-info">
            <div className="wallet-address">
              <span className="wallet-label">Connected Wallet</span>
              <span className="address">
                {address?.slice(0, 6)}...{address?.slice(-4)}
              </span>
            </div>
            <ConnectButton.Custom>
              {({ openAccountModal }) => (
                <button className="disconnect-btn" onClick={openAccountModal}>
                  Change
                </button>
              )}
            </ConnectButton.Custom>
          </div>

          <div className="message-preview">
            <span className="preview-label">MESSAGE TO SIGN</span>
            <pre className="message-content">
              {transactionData?.messageHash || "Loading..."}
            </pre>
          </div>

          {error && (
            <div className="web3-error">
              <span>⚠️ {error}</span>
            </div>
          )}

          <button
            className="web3-sign-btn"
            onClick={handleSign}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Signing...
              </>
            ) : (
              "Sign Message"
            )}
          </button>

          <div className="web3-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  // Success Screen
  if (step === "success") {
    return (
      <div className="web3-container">
        <div className="web3-card">
          <div className="web3-success-content">
            <div className="success-icon-wrapper">
              <svg className="success-check" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="3"
                  strokeDasharray="8 6"
                />
                <path
                  d="M 35 50 L 45 60 L 65 40"
                  stroke="#10b981"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </div>
            <h2 className="web3-title">Attestation Complete!</h2>
            <p className="web3-subtitle">
              Your identity has been successfully attested on-chain
            </p>
          </div>

          <div className="web3-footer">Secured by VerifiLite</div>
        </div>
      </div>
    );
  }

  return null;
}

export default Web3Connect;
